import { useState, useMemo } from "react";
import { Plus, Mail, Briefcase, Pencil, Trash2, FolderKanban, ArrowUpDown, Search, AlertCircle, CheckCircle2, Clock, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { ClientCard } from "@/components/ClientCard";
import { useClients, useCreateClient, useUpdateClient, useDeleteClient } from "@/hooks/useClients";
import { useAllClientPaymentStats } from "@/hooks/useClientPaymentStats";
import { useAllClientsNextReminders } from "@/hooks/useClientReminders";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { z } from "zod";
import type { Tables } from "@/integrations/supabase/types";
import { getRelationshipStatusLabel, getRelationshipStatusColor } from "@/lib/client-calculations";

type Client = Tables<'clients'>;

// Schema de validação com Zod
const clientSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório").max(100, "Nome muito longo"),
  email: z.string().email("Email inválido").max(255, "Email muito longo"),
  phone: z.string().max(20).optional().or(z.literal("")),
  company: z.string().max(100).optional().or(z.literal("")),
  hourly_rate: z.string().optional().or(z.literal("")),
  notes: z.string().max(1000).optional().or(z.literal("")),
  relationship_status: z.string().optional(),
  rating: z.string().optional().or(z.literal("")),
  tags: z.string().optional().or(z.literal("")),
});

interface ClientFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  hourly_rate: string;
  notes: string;
  relationship_status: string;
  rating: string;
  tags: string;
}

type SortKey = "name" | "email" | "company" | "activeProjects" | "totalBilled";
type SortDirection = "asc" | "desc";

// Máscara de telefone brasileiro
function formatPhoneBR(value: string): string {
  const numbers = value.replace(/\D/g, "").slice(0, 11);
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
}

export default function ClientsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: clients, isLoading } = useClients();
  const { data: clientPaymentStats } = useAllClientPaymentStats();
  const { data: nextReminders } = useAllClientsNextReminders();
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const deleteClient = useDeleteClient();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [formData, setFormData] = useState<ClientFormData>({
    name: "",
    email: "",
    phone: "",
    company: "",
    hourly_rate: "",
    notes: "",
    relationship_status: "ativo",
    rating: "",
    tags: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Filtrar e ordenar clientes
  const filteredAndSortedClients = useMemo(() => {
    let result = clients?.filter((client) =>
      client.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      client.email.toLowerCase().includes(searchValue.toLowerCase()) ||
      (client.company?.toLowerCase().includes(searchValue.toLowerCase()) ?? false)
    ) || [];

    result.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortKey) {
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "email":
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case "company":
          aValue = (a.company || "").toLowerCase();
          bValue = (b.company || "").toLowerCase();
          break;
        case "activeProjects":
          aValue = clientPaymentStats?.[a.id]?.activeProjects || 0;
          bValue = clientPaymentStats?.[b.id]?.activeProjects || 0;
          break;
        case "totalBilled":
          aValue = clientPaymentStats?.[a.id]?.totalPaid || 0;
          bValue = clientPaymentStats?.[b.id]?.totalPaid || 0;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [clients, searchValue, sortKey, sortDirection, clientPaymentStats]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: "", 
      email: "", 
      phone: "", 
      company: "", 
      hourly_rate: "", 
      notes: "",
      relationship_status: "ativo",
      rating: "",
      tags: "",
    });
    setFormErrors({});
    setEditingClient(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      phone: client.phone || "",
      company: client.company || "",
      hourly_rate: client.hourly_rate?.toString() || "",
      notes: client.notes || "",
      relationship_status: (client as any).relationship_status || "ativo",
      rating: (client as any).rating?.toString() || "",
      tags: ((client as any).tags || []).join(", "),
    });
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!user?.id) return;
    
    // Validar com Zod
    const result = clientSchema.safeParse(formData);
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          errors[err.path[0].toString()] = err.message;
        }
      });
      setFormErrors(errors);
      return;
    }

    // Validar valor hora
    if (formData.hourly_rate && parseFloat(formData.hourly_rate) < 0) {
      setFormErrors({ hourly_rate: "Valor deve ser maior ou igual a 0" });
      return;
    }

    try {
      // Parse tags from comma-separated string
      const tagsArray = formData.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      // Parse rating
      const ratingValue = formData.rating ? parseInt(formData.rating) : null;
      if (ratingValue !== null && (ratingValue < 1 || ratingValue > 5)) {
        setFormErrors({ rating: "Avaliação deve ser entre 1 e 5" });
        return;
      }

      const clientData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        company: formData.company.trim() || null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        notes: formData.notes.trim() || null,
        relationship_status: formData.relationship_status || 'ativo',
        rating: ratingValue,
        tags: tagsArray,
      };

      if (editingClient) {
        await updateClient.mutateAsync({
          id: editingClient.id,
          ...clientData,
        });
        toast({
          title: "Sucesso!",
          description: "Cliente atualizado com sucesso.",
        });
      } else {
        await createClient.mutateAsync({
          user_id: user.id,
          ...clientData,
        });
        toast({
          title: "Sucesso!",
          description: "Cliente adicionado com sucesso.",
        });
      }

      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: editingClient 
          ? "Não foi possível atualizar o cliente."
          : "Não foi possível adicionar o cliente.",
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingClient) return;

    try {
      await deleteClient.mutateAsync(deletingClient.id);
      toast({
        title: "Sucesso!",
        description: "Cliente excluído com sucesso.",
      });
      setDeletingClient(null);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível excluir o cliente.",
      });
    }
  };

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({ ...prev, phone: formatPhoneBR(value) }));
  };

  const SortableHeader = ({ label, sortKeyValue }: { label: string; sortKeyValue: SortKey }) => (
    <button
      onClick={() => handleSort(sortKeyValue)}
      className="flex items-center gap-1 hover:text-foreground transition-colors"
    >
      {label}
      <ArrowUpDown className={`h-3 w-3 ${sortKey === sortKeyValue ? 'text-primary' : 'text-muted-foreground'}`} />
    </button>
  );

  return (
    <DashboardLayout title="Clientes">
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground sm:text-base">
            Gerencie seus clientes e acompanhe o faturamento
          </p>
          <Button onClick={openCreateDialog} className="gap-2 w-full sm:w-auto">
            <Plus className="h-4 w-4" />
            Novo Cliente
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
        ) : isMobile ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar cliente..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-9"
              />
            </div>
            {filteredAndSortedClients.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {clients?.length === 0 ? "Nenhum cliente cadastrado ainda." : "Nenhum cliente encontrado."}
              </p>
            ) : (
              filteredAndSortedClients.map((client) => {
                const stats = clientPaymentStats?.[client.id];
                const nextReminder = nextReminders?.[client.id];
                return (
                  <ClientCard
                    key={client.id}
                    id={client.id}
                    name={client.name}
                    email={client.email}
                    activeProjects={stats?.activeProjects || 0}
                    totalBilled={stats?.totalPaid || 0}
                    totalPending={stats?.totalPending || 0}
                    totalOverdue={stats?.totalOverdue || 0}
                    relationshipStatus={(client as any).relationship_status}
                    rating={(client as any).rating}
                    tags={(client as any).tags}
                    nextReminder={nextReminder ? {
                      title: nextReminder.title,
                      reminder_date: nextReminder.reminder_date,
                      reminder_time: nextReminder.reminder_time,
                      reminder_type: nextReminder.reminder_type,
                    } : undefined}
                    onClick={() => navigate(`/clients/${client.id}`)}
                    onEdit={() => openEditDialog(client)}
                    onDelete={() => setDeletingClient(client)}
                  />
                );
              })
            )}
          </div>
        ) : (
          <Card className="shadow-card">
            <CardContent className="p-6">
              {clients?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum cliente cadastrado ainda. Clique em "Novo Cliente" para começar.
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Buscar cliente..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="rounded-lg border overflow-hidden">
                    <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="font-semibold">
                              <SortableHeader label="Cliente" sortKeyValue="name" />
                            </TableHead>
                            <TableHead className="font-semibold">Status</TableHead>
                            <TableHead className="font-semibold">Avaliação</TableHead>
                            <TableHead className="font-semibold">Tags</TableHead>
                            <TableHead className="font-semibold">
                              <SortableHeader label="Projetos" sortKeyValue="activeProjects" />
                            </TableHead>
                            <TableHead className="font-semibold">
                              <SortableHeader label="Recebido" sortKeyValue="totalBilled" />
                            </TableHead>
                            <TableHead className="font-semibold">Pagamentos</TableHead>
                            <TableHead className="font-semibold text-right">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAndSortedClients.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                                Nenhum cliente encontrado.
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredAndSortedClients.map((client) => {
                              const initials = client.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase();
                              const stats = clientPaymentStats?.[client.id] || { 
                                activeProjects: 0, 
                                totalPaid: 0, 
                                totalPending: 0, 
                                totalOverdue: 0,
                                totalProjects: 0, 
                                paymentsCount: 0 
                              };
                              const relationshipStatus = (client as any).relationship_status || 'ativo';
                              const rating = (client as any).rating;
                              const tags: string[] = (client as any).tags || [];
                              
                              return (
                                <TableRow key={client.id} className="hover:bg-muted/50">
                                  <TableCell>
                                    <button
                                      onClick={() => navigate(`/clients/${client.id}`)}
                                      className="flex items-center gap-3 hover:text-primary transition-colors"
                                    >
                                      <Avatar className="h-8 w-8">
                                        <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                                          {initials}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div className="text-left">
                                        <span className="font-medium block">{client.name}</span>
                                        {client.company && (
                                          <span className="text-xs text-muted-foreground">{client.company}</span>
                                        )}
                                      </div>
                                    </button>
                                  </TableCell>
                                  <TableCell>
                                    <Badge className={getRelationshipStatusColor(relationshipStatus)}>
                                      {getRelationshipStatusLabel(relationshipStatus)}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    {rating ? (
                                      <div className="flex items-center gap-0.5">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <Star
                                            key={star}
                                            className={`h-3.5 w-3.5 ${
                                              star <= rating
                                                ? 'fill-warning text-warning'
                                                : 'text-muted-foreground/30'
                                            }`}
                                          />
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-muted-foreground text-sm">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {tags.length > 0 ? (
                                      <TooltipProvider>
                                        <div className="flex flex-wrap gap-1 max-w-[150px]">
                                          {tags.slice(0, 2).map((tag: string, index: number) => (
                                            <Badge key={index} variant="outline" className="text-xs">
                                              {tag}
                                            </Badge>
                                          ))}
                                          {tags.length > 2 && (
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Badge variant="outline" className="text-xs text-muted-foreground cursor-help">
                                                  +{tags.length - 2}
                                                </Badge>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                {tags.slice(2).join(', ')}
                                              </TooltipContent>
                                            </Tooltip>
                                          )}
                                        </div>
                                      </TooltipProvider>
                                    ) : (
                                      <span className="text-muted-foreground text-sm">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="secondary" className="gap-1">
                                      <FolderKanban className="h-3 w-3" />
                                      {stats.activeProjects}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <span className="font-medium text-success">
                                      R$ {(stats.totalPaid || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <TooltipProvider>
                                      <div className="flex items-center gap-1">
                                        {stats.totalPending > 0 && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Badge variant="secondary" className="gap-1 text-xs">
                                                <Clock className="h-3 w-3" />
                                                R$ {stats.totalPending.toLocaleString("pt-BR", { minimumFractionDigits: 0 })}
                                              </Badge>
                                            </TooltipTrigger>
                                            <TooltipContent>Pendente</TooltipContent>
                                          </Tooltip>
                                        )}
                                        {stats.totalOverdue > 0 && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Badge variant="destructive" className="gap-1 text-xs">
                                                <AlertCircle className="h-3 w-3" />
                                                R$ {stats.totalOverdue.toLocaleString("pt-BR", { minimumFractionDigits: 0 })}
                                              </Badge>
                                            </TooltipTrigger>
                                            <TooltipContent>Atrasado</TooltipContent>
                                          </Tooltip>
                                        )}
                                        {stats.totalPending === 0 && stats.totalOverdue === 0 && (
                                          <span className="text-muted-foreground text-sm">-</span>
                                        )}
                                      </div>
                                    </TooltipProvider>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openEditDialog(client)}
                                        className="h-8 w-8"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setDeletingClient(client)}
                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Form Content - shared between Dialog and Drawer */}
      {isMobile ? (
        <Drawer open={isDialogOpen} onOpenChange={(open) => {
          if (!open) resetForm();
          setIsDialogOpen(open);
        }}>
          <DrawerContent className="max-h-[90vh]">
            <DrawerHeader className="text-left">
              <DrawerTitle>{editingClient ? "Editar Cliente" : "Novo Cliente"}</DrawerTitle>
              <DrawerDescription>
                {editingClient ? "Atualize as informações do cliente" : "Adicione um novo cliente ao seu portfólio"}
              </DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-4 overflow-y-auto">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name-mobile">Nome *</Label>
                  <Input 
                    id="name-mobile" 
                    placeholder="Nome da empresa ou pessoa"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className={formErrors.name ? "border-destructive" : ""}
                  />
                  {formErrors.name && <p className="text-sm text-destructive">{formErrors.name}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email-mobile">Email *</Label>
                  <Input 
                    id="email-mobile" 
                    type="email" 
                    placeholder="email@empresa.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className={formErrors.email ? "border-destructive" : ""}
                  />
                  {formErrors.email && <p className="text-sm text-destructive">{formErrors.email}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone-mobile">Telefone</Label>
                  <Input 
                    id="phone-mobile" 
                    placeholder="(11) 99999-9999"
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hourly_rate-mobile">Valor Hora (R$)</Label>
                  <Input 
                    id="hourly_rate-mobile" 
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="100.00"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                    className={formErrors.hourly_rate ? "border-destructive" : ""}
                  />
                  {formErrors.hourly_rate && <p className="text-sm text-destructive">{formErrors.hourly_rate}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-mobile">Empresa</Label>
                  <Input 
                    id="company-mobile" 
                    placeholder="Nome da empresa (opcional)"
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes-mobile">Notas</Label>
                  <Textarea 
                    id="notes-mobile" 
                    placeholder="Observações sobre o cliente..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="min-h-[80px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="relationship_status-mobile">Status do Relacionamento</Label>
                  <select
                    id="relationship_status-mobile"
                    value={formData.relationship_status}
                    onChange={(e) => setFormData(prev => ({ ...prev, relationship_status: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                    <option value="prospect">Prospect</option>
                    <option value="perdido">Perdido</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rating-mobile">Avaliação (1-5)</Label>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          rating: prev.rating === star.toString() ? "" : star.toString() 
                        }))}
                        className="p-1 hover:bg-muted rounded"
                      >
                        <Star
                          className={`h-5 w-5 ${
                            parseInt(formData.rating || "0") >= star
                              ? 'fill-warning text-warning'
                              : 'text-muted-foreground/30'
                          }`}
                        />
                      </button>
                    ))}
                    {formData.rating && (
                      <span className="text-sm text-muted-foreground ml-2">
                        {formData.rating}/5
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tags-mobile">Tags</Label>
                  <Input 
                    id="tags-mobile" 
                    placeholder="Ex: VIP, Recorrente, Novo (separadas por vírgula)"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">Separe as tags por vírgula</p>
                </div>
              </div>
            </div>
            <DrawerFooter className="pt-2">
              <Button 
                onClick={handleSubmit} 
                disabled={createClient.isPending || updateClient.isPending}
              >
                {(createClient.isPending || updateClient.isPending) 
                  ? "Salvando..." 
                  : editingClient ? "Salvar Alterações" : "Adicionar Cliente"}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline">Cancelar</Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          if (!open) resetForm();
          setIsDialogOpen(open);
        }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="shrink-0">
              <DialogTitle>{editingClient ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
              <DialogDescription>
                {editingClient ? "Atualize as informações do cliente" : "Adicione um novo cliente ao seu portfólio"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input 
                  id="name" 
                  placeholder="Nome da empresa ou pessoa"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={formErrors.name ? "border-destructive" : ""}
                />
                {formErrors.name && <p className="text-sm text-destructive">{formErrors.name}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="email@empresa.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={formErrors.email ? "border-destructive" : ""}
                />
                {formErrors.email && <p className="text-sm text-destructive">{formErrors.email}</p>}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input 
                    id="phone" 
                    placeholder="(11) 99999-9999"
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hourly_rate">Valor Hora (R$)</Label>
                  <Input 
                    id="hourly_rate" 
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="100.00"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                    className={formErrors.hourly_rate ? "border-destructive" : ""}
                  />
                  {formErrors.hourly_rate && <p className="text-sm text-destructive">{formErrors.hourly_rate}</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Empresa</Label>
                <Input 
                  id="company" 
                  placeholder="Nome da empresa (opcional)"
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea 
                  id="notes" 
                  placeholder="Observações sobre o cliente..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="relationship_status">Status do Relacionamento</Label>
                  <select
                    id="relationship_status"
                    value={formData.relationship_status}
                    onChange={(e) => setFormData(prev => ({ ...prev, relationship_status: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                    <option value="prospect">Prospect</option>
                    <option value="perdido">Perdido</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rating">Avaliação</Label>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          rating: prev.rating === star.toString() ? "" : star.toString() 
                        }))}
                        className="p-1 hover:bg-muted rounded"
                      >
                        <Star
                          className={`h-5 w-5 ${
                            parseInt(formData.rating || "0") >= star
                              ? 'fill-warning text-warning'
                              : 'text-muted-foreground/30'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tags">Tags</Label>
                <Input 
                  id="tags" 
                  placeholder="Ex: VIP, Recorrente, Novo (separadas por vírgula)"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">Separe as tags por vírgula</p>
              </div>
            </div>
            <DialogFooter className="shrink-0 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={createClient.isPending || updateClient.isPending}
              >
                {(createClient.isPending || updateClient.isPending) 
                  ? "Salvando..." 
                  : editingClient ? "Salvar Alterações" : "Adicionar Cliente"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Confirmação Exclusão */}
      <AlertDialog open={!!deletingClient} onOpenChange={(open) => !open && setDeletingClient(null)}>
        <AlertDialogContent className="max-w-[calc(100vw-2rem)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cliente "{deletingClient?.name}"? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <AlertDialogCancel className="w-full sm:w-auto">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteClient.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
